import { useCollapsedState } from "components/DetailPage/hooks";
import { FilterProvider } from "components/filters/FilterContext";
import { GoogleMapPlanning, GoogleMapPlanningRef } from "components/GoogleMap/GoogleMapPlanning";
import { useOpenShowSidePanel } from "components/SidePanel/Show/ShowSidePanel";
import { useOpenCompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import TwoColumnLayout from "layout/TwoColumnLayout";
import moment from "moment";
import React, { useCallback, useRef, useState } from "react";
import { mapFilterValuesToInstruction as mapShowFilterValuesToInstruction, mapResponseToFilterValues as mapShowResponseToFilterValues } from "show/list/helpers";
import { useDebounce, useLocalStorage } from "utilities/usehooks";
import { mapFilterValuesToInstruction as mapVenueFilterValuesToInstruction, mapResponseToFilterValues as mapVenueResponseToFilterValues } from "venue/list/helpers";
import { allFilterLayers, FilterLayerType, getListUniqueKey, getLocation, isAirportItem, isShowItem, isVenueItem, PlanningItem, planningItemIsOnLocation, sortPlanningItemWithFilteredInFront } from "./planningMapHelpers";
import PlanningMapShowList from "./PlanningMapShowList";
import PlanningMapVenueList from "./PlanningMapVenueList";
import { usePlanningMapEntity } from "./hooks";
import PlanningMapAirportList from "./PlanningMapAirportList";
import { useOpenAirportSidePanel } from "components/SidePanel/Airport/AirportSidePanel";
import { mapAirportSizeFilterToInstruction, mapResponseToAirportSizeFilter } from "components/filters/mappingUtils";
import { useLoaderData } from "react-router";

type PlanningItemsInBounds = {
  shows: server.dto.ShowForPlanningMap[]
  hoveredShows: server.dto.ShowForPlanningMap[]
  selectedShows: server.dto.ShowForPlanningMap[]

  venues: server.dto.VenueSmall[]
  hoveredVenues: server.dto.VenueSmall[]
  selectedVenues: server.dto.VenueSmall[]

  airports: server.dto.Airport[]
  hoveredAirports: server.dto.Airport[]
  selectedAirports: server.dto.Airport[]
}

export type DisplayMode = "list" | "filter";

export const PlanningMapApp = () => {
  const viewModel = useLoaderData<server.viewmodel.MapViewForInternalFrontModel>();
  const [visibleLayers, setVisibleLayers] = useLocalStorage<FilterLayerType[]>("planning:map:visible-layers", ["show"]);
  const [showDisplayMode, setShowDisplayMode] = useState<DisplayMode>("list");
  const [venueDisplayMode, setVenueDisplayMode] = useState<DisplayMode>("list");
  const [airportDisplayMode, setAirportDisplayMode] = useState<DisplayMode>("list");
  const hasLayer = (layer: FilterLayerType) => visibleLayers.some(l => l === layer);

  const openCompanySidePanel = useCallback(useOpenCompanySidePanel(), []);
  const openShowSidePanel = useCallback(useOpenShowSidePanel(), []);
  const openAirportSidePanel = useCallback(useOpenAirportSidePanel(), []);

  const collapsedStates = allFilterLayers.reduce<Record<FilterLayerType, ReturnType<typeof useCollapsedState>>>((acc, layer) => {
    return { ...acc, [layer]: useCollapsedState(getListUniqueKey(layer)) }
  }, {} as any);

  const mapRef = useRef<GoogleMapPlanningRef>(null);

  const [selectedLocation, setSelectedLocation] = useState<server.dto.Geo>(undefined);
  const [hoveredLocation, setHoveredLocation] = useState<server.dto.Geo>(undefined);

  const [googleMapBounds, setGoogleMapBounds] = useState<google.maps.LatLngBounds>();

  const debouncedGoogleMapBounds = useDebounce(googleMapBounds, 600);

  const venueApi = usePlanningMapEntity({
    filterLayer: "venue",
    visibleLayers,
    filterSet: "planning_Map_Venue",
    api: window.CoreApi.PlanningMap.PaginatedVenue,
    initialInstruction: viewModel.venue_InitialInstruction,
    mapFilterValuesToInstruction: mapVenueFilterValuesToInstruction,
    mapResponseToFilterValues: mapVenueResponseToFilterValues,
    defaultFilters: viewModel.venue_DefaultFilters
  });

  const showApi = usePlanningMapEntity({
    filterLayer: "show",
    visibleLayers,
    filterSet: "planning_Map_Show",
    api: window.CoreApi.PlanningMap.PaginatedShow,
    initialInstruction: viewModel.show_InitialInstruction,
    mapFilterValuesToInstruction: mapShowFilterValuesToInstruction,
    mapResponseToFilterValues: mapShowResponseToFilterValues,
    defaultFilters: viewModel.show_DefaultFilters
  });

  const airportApi = usePlanningMapEntity({
    filterLayer: "airport",
    visibleLayers,
    filterSet: "planning_Map_Airport",
    api: window.CoreApi.PlanningMap.PaginatedAirport,
    initialInstruction: viewModel.airport_InitialInstruction,
    mapFilterValuesToInstruction: (filterSet, filter, page, perPage) => ({ cache: filterSet, page, perPage, airportSize: mapAirportSizeFilterToInstruction(filter.airportSize) }),
    mapResponseToFilterValues: (response: server.dto.ListAirportFilters) => ({ airportSize: mapResponseToAirportSizeFilter(response.airportSize) }),
    defaultFilters: viewModel.airport_DefaultFilters
  });


  const onBoundsChange = (bounds: google.maps.LatLngBounds) => {
    setGoogleMapBounds(bounds);
  }

  const onMarkersClicked = (items: PlanningItem[]) => {
    setSelectedLocation(getLocation(items?.[0]));
    if (items) {
      setShowDisplayMode("list");
      setVenueDisplayMode("list");
      setAirportDisplayMode("list");
      openSidePanel(items);
    }
  }

  const openLayer = (layer: FilterLayerType) => {
    collapsedStates[layer].setCollapsed(false);

    setVisibleLayers([...visibleLayers, layer]);
  }

  const closeLayer = (layer: FilterLayerType) => {
    collapsedStates[layer].setCollapsed(true);

    resetInitialLoadingForLayer(layer);
    setVisibleLayers(visibleLayers.filter(l => l !== layer));
  }

  const resetInitialLoadingForLayer = (layer: FilterLayerType) => {
    switch (layer) {
      case "show":
        showApi.setInitialLoading(true);
        break;
      case "venue":
        venueApi.setInitialLoading(true);
        break;
      case "airport":
        airportApi.setInitialLoading(true);
        break;
    }
  }

  const onLayerToggle = (layer: FilterLayerType) => {
    const isVisible = visibleLayers.some(l => l === layer);
    if (isVisible) {
      closeLayer(layer);
    } else {
      openLayer(layer);
    }
  }

  const openSidePanel = (items: PlanningItem[]) => {
    const venues = items.filter(isVenueItem).sort((a, b) => a.name.localeCompare(b.name));
    const shows = items.filter(isShowItem).sort((a, b) => {
      const momentA = moment.utc(a.date);
      const momentB = moment.utc(b.date);
      return momentA.isBefore(momentB) ? -1 : momentA.isAfter(momentB) ? 1 : 0;
    });
    const airports = items.filter(item => isAirportItem(item));

    if (venues.length > 0) {
      openCompanySidePanel(venues[0].venueId, "venue");
    } else if (shows.length > 0) {
      openShowSidePanel(shows[0].showId);
    } else if (airports.length > 0) {
      openAirportSidePanel(airports[0].airportId);
    }
  };

  const allShows: server.dto.ShowForPlanningMap[] = hasLayer("show") ? (showApi.query.data.items) : [];
  const allShowsCount = allShows.length;
  const showError = hasLayer("show") ? showApi.query.data.error : undefined;

  const allVenues: server.dto.VenueSmall[] = hasLayer("venue") ? (venueApi.query.data.items) : [];
  const allVenuesCount = allVenues.length;
  const venueError = hasLayer("venue") ? venueApi.query.data.error : undefined;

  const allAirports: server.dto.Airport[] = hasLayer("airport") ? (airportApi.query.data.items) : [];
  const allAirportsCount = allAirports.length;
  const airportError = hasLayer("airport") ? airportApi.query.data.error : undefined;

  const listItems = React.useMemo<PlanningItemsInBounds>(() => {

    let hoveredShows: server.dto.ShowForPlanningMap[] = [];
    let hoveredVenues: server.dto.VenueSmall[] = [];
    let hoveredAirports: server.dto.Airport[] = [];

    if (hoveredLocation) {
      hoveredShows = allShows.filter(show => planningItemIsOnLocation(show, hoveredLocation));
      hoveredVenues = allVenues.filter(venue => planningItemIsOnLocation(venue, hoveredLocation));
      hoveredAirports = allAirports.filter(airport => planningItemIsOnLocation(airport, hoveredLocation));
    }

    // If the google map bounds are not known yet (e.g. during initialization of the map), show all items
    if (!debouncedGoogleMapBounds) {
      return {
        shows: allShows,
        hoveredShows,
        selectedShows: [],

        venues: allVenues,
        hoveredVenues,
        selectedVenues: [],

        airports: allAirports,
        hoveredAirports,
        selectedAirports: []
      }
    }

    // Only show items within the bounds of the map viewport
    const showItems = allShows
      .filter(show => debouncedGoogleMapBounds.contains(new google.maps.LatLng(show.point.lat, show.point.lng)));

    const venueItems = allVenues
      .filter(venue => debouncedGoogleMapBounds.contains(new google.maps.LatLng(venue.location.lat, venue.location.lng)));

    const airportItems = allAirports
      .filter(airport => debouncedGoogleMapBounds.contains(new google.maps.LatLng(airport.point.lat, airport.point.lng)));

    // If we clicked on a marker, show only the items on that marker
    if (selectedLocation) {
      const showsWithFilteredInFront = sortPlanningItemWithFilteredInFront(showItems, selectedLocation);
      const venuesWithFilteredInFront = sortPlanningItemWithFilteredInFront(venueItems, selectedLocation);
      const airportsWithFilteredInFront = sortPlanningItemWithFilteredInFront(airportItems, selectedLocation);

      return {
        shows: showsWithFilteredInFront,
        hoveredShows,
        selectedShows: showItems.filter(show => planningItemIsOnLocation(show, selectedLocation)),
        venues: venuesWithFilteredInFront,
        hoveredVenues,
        selectedVenues: venueItems.filter(venue => planningItemIsOnLocation(venue, selectedLocation)),
        airports: airportsWithFilteredInFront,
        hoveredAirports,
        selectedAirports: airportItems.filter(airport => planningItemIsOnLocation(airport, selectedLocation))
      }
    }

    return {
      shows: showItems,
      hoveredShows,
      selectedShows: [],
      venues: venueItems,
      hoveredVenues,
      selectedVenues: [],
      airports: airportItems,
      hoveredAirports,
      selectedAirports: []
    }
  }, [showApi.query.data, venueApi.query.data, airportApi.query.data, debouncedGoogleMapBounds, selectedLocation, hoveredLocation, visibleLayers]);

  const isLoading = showApi.query.isFetching || venueApi.query.isFetching;

  const zoomToFitAll = () => {
    mapRef.current.zoomToFit();
  }

  const onListItemClicked = (item: PlanningItem) => {
    setSelectedLocation(getLocation(item));
  }

  const onHoverListItem = (item: PlanningItem) => {
    if (item) {
      setHoveredLocation(getLocation(item));
      mapRef.current.bounceMarkerOnce(getLocation(item));
    } else {
      setHoveredLocation(undefined);
    }
  }

  return <>
    <TwoColumnLayout style={{ height: "100%" }}>
      <TwoColumnLayout.LeftColumn>
        <GoogleMapPlanning
          ref={mapRef}
          isLoading={isLoading}
          shows={allShows}
          venues={allVenues}
          airports={allAirports}
          selectedLocation={selectedLocation}
          onSelectedLocationChange={setSelectedLocation}
          hoveredLocation={hoveredLocation}
          onHoveredLocationChange={setHoveredLocation}

          onBoundsChange={onBoundsChange}
          onMarkerClicked={onMarkersClicked}
        />
      </TwoColumnLayout.LeftColumn>
      {/* We need to fix the width of the right side to make the list work */}
      <TwoColumnLayout.RightColumn style={{ overflowY: "auto", width: 332, minWidth: 332, maxWidth: 332, paddingRight: 20 }}>
        <div className="flex-column gap-larger">
          <FilterProvider
            ref={showApi.filterRef}
            filterPopupPosition="left"
            onFilterChange={showApi.onFilterChange}
          >
            <PlanningMapShowList
              onSidePanelOpen={onListItemClicked}
              showStatuses={viewModel.showStatuses}
              showTags={viewModel.showTags}
              displayMode={showDisplayMode}
              isInitialLoading={showApi.initialLoading}
              error={showError}
              onDisplayModeChange={setShowDisplayMode}
              isLayerSelected={hasLayer("show")}
              onLayerToggle={() => onLayerToggle("show")}
              onViewAllClick={() => zoomToFitAll()}
              shows={listItems.shows}
              selectedShows={listItems.selectedShows}
              total={allShowsCount}
              hoveredShows={listItems.hoveredShows}
              onHover={onHoverListItem}
              isFetching={showApi.query.isFetching}
            />
          </FilterProvider>
          <FilterProvider
            ref={venueApi.filterRef}
            filterPopupPosition="left"
            onFilterChange={venueApi.onFilterChange}
          >
            <PlanningMapVenueList
              onSidePanelOpen={onListItemClicked}
              displayMode={venueDisplayMode}
              isInitialLoading={venueApi.initialLoading}
              error={venueError}
              onDisplayModeChange={setVenueDisplayMode}
              isLayerSelected={hasLayer("venue")}
              onLayerToggle={() => onLayerToggle("venue")}
              onViewAllClick={() => zoomToFitAll()}
              tags={viewModel.venueTags}
              venues={listItems.venues}
              selectedVenues={listItems.selectedVenues}
              total={allVenuesCount}
              hoveredVenues={listItems.hoveredVenues}
              onHover={onHoverListItem}
              isFetching={venueApi.query.isFetching}
            />
          </FilterProvider>

          <FilterProvider
            ref={airportApi.filterRef}
            filterPopupPosition="left"
            onFilterChange={airportApi.onFilterChange}
          >
            <PlanningMapAirportList
              onSidePanelOpen={onListItemClicked}
              displayMode={airportDisplayMode}
              isInitialLoading={airportApi.initialLoading}
              error={airportError}
              onDisplayModeChange={setAirportDisplayMode}
              isLayerSelected={hasLayer("airport")}
              onLayerToggle={() => onLayerToggle("airport")}
              onViewAllClick={() => zoomToFitAll()}
              airports={listItems.airports}
              selectedAirports={listItems.selectedAirports}
              total={allAirportsCount}
              hoveredAirports={listItems.hoveredAirports}
              onHover={onHoverListItem}
              isFetching={airportApi.query.isFetching}
            />
          </FilterProvider>
        </div>
      </TwoColumnLayout.RightColumn>
    </TwoColumnLayout>
  </>
}
